import React, { useState } from "react";
import { Button, Typography } from "@mui/material";
import {
  IconButton,
  Radio,
  TableBody,
  TableCell,
  TableCellRow,
  Tooltip,
} from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Delete02Icon,
  HelpCircleIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import Role from "../../../../enums/Role";
import ApprovalsDrawer from "../ApprovalsDrawer";
import SettingPolicyType from "../../../../enums/SettingPolicyType";
import useSettingPolicyForm from "../../../../hooks/useSettingPolicyForm";
import {
  APPROVAL_COMBINATIONS,
  ApprovalPolicy,
} from "../../../../hooks/settingPolicy.types";
import { Inline } from "components/layout/reusableLayouts";
import { toast } from "react-toastify";

export const POLICY_COLUMN_COUNT = 7;

interface PolicyTableSectionProps {
  type: SettingPolicyType;
  policies: ApprovalPolicy[];
  title: string;
  approvalsDrawerDescription: string;
  tooltipDescription?: string;
  refetch: () => void;
}

const PolicyTableSection: React.FC<PolicyTableSectionProps> = ({
  type,
  policies,
  title,
  approvalsDrawerDescription,
  tooltipDescription,
  refetch,
}) => {
  const { t } = useTranslation();

  const [openApprovalsDrawer, setOpenApprovalsDrawer] =
    useState<boolean>(false);
  const [selectedSettingPolicyType, setSelectedSettingPolicyType] =
    useState<SettingPolicyType | null>(null);
  const [updatingRows, setUpdatingRows] = useState<{ [id: string]: boolean }>(
    {},
  );

  // Sort policies by user role
  const sortedPolicies = [...policies].sort((a, b) =>
    a.userRole.localeCompare(b.userRole),
  );

  const setRowUpdating = (id: string, value: boolean) => {
    setUpdatingRows((prev) => ({ ...prev, [id]: value }));
  };

  const handleOpenApprovalsDrawer = (settingPolicyType: SettingPolicyType) => {
    setOpenApprovalsDrawer(true);
    setSelectedSettingPolicyType(settingPolicyType);
  };

  const handleCloseApprovalsDrawer = (isRefetch?: boolean) => {
    if (isRefetch) {
      refetch();
    }
    setOpenApprovalsDrawer(false);
    setSelectedSettingPolicyType(null);
  };

  const { handleDeletePolicy, handleUpdateSettingPolicy } =
    useSettingPolicyForm({
      settingPolicyType: type,
      onCompleted: (policyId?: string) => {
        toast.dismiss();
        refetch();
        if (policyId) {
          setRowUpdating(policyId, false);
        }
      },
      onError: (error, policyId?: string) => {
        toast.dismiss();
        if (policyId) {
          setRowUpdating(policyId, false);
        }
      },
    });

  const handleUpdateApprovalRow = (
    policy: ApprovalPolicy,
    newRoles: Role[],
  ) => {
    setRowUpdating(policy.id, true);
    handleUpdateSettingPolicy({ policy, newRoles });
  };

  const handleDelete = (policyId: string) => {
    setRowUpdating(policyId, true);
    handleDeletePolicy(policyId);
  };

  return (
    <>
      <TableBody>
        <TableCellRow>
          <TableCell colSpan={POLICY_COLUMN_COUNT}>
            <Inline justify="between" wrap={false} sx={{ width: "100%" }}>
              <Inline gap="var(--space-3)" wrap={false}>
                <Typography variant="h6" component="h2">
                  {title}
                </Typography>
                {tooltipDescription && (
                  <Tooltip title={tooltipDescription}>
                    <HugeiconsIcon icon={HelpCircleIcon} size={16} />
                  </Tooltip>
                )}
              </Inline>
              <Button
                variant="primary-filled"
                size="small"
                onClick={() => handleOpenApprovalsDrawer(type)}
              >
                {t("Add")}
              </Button>
            </Inline>
          </TableCell>
        </TableCellRow>

        {sortedPolicies.map((policy) => {
          const isUpdating = !!updatingRows[policy.id];
          // Only an actual array counts: a missing approvalRoles leaves every
          // option unticked, which is what the old per-cell checks did.
          const roles = Array.isArray(policy.policy?.approvalRoles)
            ? policy.policy.approvalRoles
            : null;

          return (
            <TableCellRow
              key={policy.id}
              style={{ opacity: isUpdating ? 0.5 : 1 }}
            >
              <TableCell>{policy.userRole}</TableCell>

              {APPROVAL_COMBINATIONS.map((combination) =>
                combination.workflowOnly &&
                type !== SettingPolicyType.WORKFLOW_APPROVAL ? (
                  <TableCell key={combination.key} />
                ) : (
                  <TableCell key={combination.key} align="center">
                    <Radio
                      name={`approval-roles-${policy.id}`}
                      aria-label={t(combination.label)}
                      checked={roles !== null && combination.matches(roles)}
                      onChange={() =>
                        handleUpdateApprovalRow(policy, combination.roles)
                      }
                      disabled={isUpdating}
                    />
                  </TableCell>
                ),
              )}

              <TableCell align="center">
                <IconButton
                  aria-label={t("Delete")}
                  color="danger"
                  variant="soft"
                  size="small"
                  onClick={() => handleDelete(policy.id)}
                  disabled={isUpdating}
                >
                  <HugeiconsIcon icon={Delete02Icon} size={16} />
                </IconButton>
              </TableCell>
            </TableCellRow>
          );
        })}
      </TableBody>

      {selectedSettingPolicyType != null && (
        <ApprovalsDrawer
          open={openApprovalsDrawer}
          handleCloseDrawer={handleCloseApprovalsDrawer}
          settingPolicyType={selectedSettingPolicyType}
          title={title}
          description={approvalsDrawerDescription}
        />
      )}
    </>
  );
};

export default PolicyTableSection;
